import React, { useRef, useState } from 'react';
import { Button, DatePicker, Input, Modal, Radio, Table, Tag, Typography, message } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import { toPng } from 'html-to-image';
import dayjs from 'dayjs';
import { useMinibatchDay, useMinibatchReport } from '../../hooks/useStudioOps';
import { useGetMergedGuidelinesForShoot } from '../../hooks/useShootMergeGuideline';
import ExportExcel from '../../commonComponents/Export-Excel';
import BayHistogram, { ConsistencyDial } from '../../components/LiveShoots/BayHistogram';
import styles from './index.module.css';

const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
const CET_ZONE = 'Europe/Berlin';
const SCAN_BUCKETS = [{ key: 'scans', label: 'Scans', color: '#2e90fa' }];

function formatScanTime(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
    });
}

function formatDurationMs(ms) {
    if (typeof ms !== 'number' || ms < 0 || Number.isNaN(ms)) return '—';
    const minutes = Math.round(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours && mins) return `${hours}h ${mins}m`;
    if (hours) return `${hours}h`;
    return `${mins}m`;
}

function formatScanDuration(from, to) {
    const start = from ? new Date(from) : null;
    const end = to ? new Date(to) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return '—';
    return formatDurationMs(end - start);
}

function sumNum(rows, key) {
    return (rows || []).reduce((total, row) => (typeof row[key] === 'number' ? total + row[key] : total), 0);
}

function earliest(rows, key) {
    return (rows || []).reduce((min, row) => {
        if (!row[key]) return min;
        if (!min || new Date(row[key]) < new Date(min)) return row[key];
        return min;
    }, null);
}

function latest(rows, key) {
    return (rows || []).reduce((max, row) => {
        if (!row[key]) return max;
        if (!max || new Date(row[key]) > new Date(max)) return row[key];
        return max;
    }, null);
}

function numSort(key) {
    return (a, b) => (a[key] ?? -1) - (b[key] ?? -1);
}

function textSort(pick) {
    return (a, b) => String(pick(a) || '').localeCompare(String(pick(b) || ''));
}

function dateSort(key) {
    return (a, b) => new Date(a[key] || 0) - new Date(b[key] || 0);
}

function durationMs(row) {
    const start = row.firstScanAt ? new Date(row.firstScanAt) : null;
    const end = row.lastScanAt ? new Date(row.lastScanAt) : null;
    if (!start || !end || Number.isNaN(+start) || Number.isNaN(+end) || end < start) return -1;
    return end - start;
}

function totalDurationMs(rows) {
    let any = false;
    const total = (rows || []).reduce((sum, row) => {
        const ms = durationMs(row);
        if (ms < 0) return sum;
        any = true;
        return sum + ms;
    }, 0);
    return any ? total : null;
}

function operatorName(row) {
    return row.operator?.name || row.operator?.email || '';
}

function operatorType(row) {
    if (row.operator?.affiliation === 'agency') return 'Agency';
    if (row.operator?.affiliation === 'fx') return 'FX team';
    return '';
}

function operatorContact(row) {
    if (row.operator?.affiliation === 'agency') return row.operator?.agency || '';
    return row.operator?.email || '';
}

function operatorSheet(rows) {
    const total = sumNum(rows, 'total');
    const found = sumNum(rows, 'foundOwn') + sumNum(rows, 'foundOtherBay');
    const first = earliest(rows, 'firstScanAt');
    const last = latest(rows, 'lastScanAt');
    const firstKey = `First scan (${localTimeZone})`;
    const lastKey = `Last scan (${localTimeZone})`;
    const data = rows.map((row) => ({
        Operator: operatorName(row) || '—',
        Type: operatorType(row) || '—',
        'Agency / Email': operatorContact(row) || '—',
        Scans: row.total || 0,
        'Found own': row.foundOwn || 0,
        'Other bay': row.foundOtherBay || 0,
        'Not found': row.notFound || 0,
        'Not sent to Bay': row.notSentToBay || 0,
        'Success %': row.successRate || 0,
        [firstKey]: formatScanTime(row.firstScanAt),
        [lastKey]: formatScanTime(row.lastScanAt),
        Duration: formatScanDuration(row.firstScanAt, row.lastScanAt),
    }));
    data.push({
        Operator: 'Total',
        Type: '',
        'Agency / Email': '',
        Scans: total,
        'Found own': sumNum(rows, 'foundOwn'),
        'Other bay': sumNum(rows, 'foundOtherBay'),
        'Not found': sumNum(rows, 'notFound'),
        'Not sent to Bay': sumNum(rows, 'notSentToBay'),
        'Success %': total ? Number(((found / total) * 100).toFixed(1)) : 0,
        [firstKey]: formatScanTime(first),
        [lastKey]: formatScanTime(last),
        Duration: formatDurationMs(totalDurationMs(rows)),
    });
    return data;
}

function guidelineSheet(rows) {
    const expected = sumNum(rows, 'expectedSkus');
    const combined = sumNum(rows, 'combinedFound');
    const data = rows.map((row) => ({
        Guideline: row.guidelineName || '—',
        'SKUs on guideline': row.expectedSkus ?? '',
        'Found here': row.foundOwn || 0,
        'Found from other scan': row.foundFromOther || 0,
        'Combined found': row.combinedFound || 0,
        Remaining: row.remaining ?? '',
        'Combined %': row.combinedPct == null ? '' : row.combinedPct,
    }));
    data.push({
        Guideline: 'Total',
        'SKUs on guideline': expected,
        'Found here': sumNum(rows, 'foundOwn'),
        'Found from other scan': sumNum(rows, 'foundFromOther'),
        'Combined found': combined,
        Remaining: sumNum(rows, 'remaining'),
        'Combined %': expected ? Number(((combined / expected) * 100).toFixed(1)) : '',
    });
    return data;
}

function sortedRows(rows, sorter) {
    const active = Array.isArray(sorter) ? sorter.find((item) => item.order) : sorter;
    const compare = active && active.column && active.column.sorter;
    if (!compare || !active.order) return rows;
    const next = [...rows].sort(compare);
    return active.order === 'descend' ? next.reverse() : next;
}

function fileSlug(name) {
    return String(name || 'minibatch').replace(/[^\w.-]+/g, '_').slice(0, 80);
}

function cetShiftDate(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: CET_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(now);
    const pick = (type) => parts.find((part) => part.type === type).value;
    const hour = Number(pick('hour'));
    const minute = Number(pick('minute'));
    const calendar = `${pick('year')}-${pick('month')}-${pick('day')}`;
    if (hour > 6 || (hour === 6 && minute >= 30)) return calendar;
    const previous = new Date(`${calendar}T12:00:00Z`);
    previous.setUTCDate(previous.getUTCDate() - 1);
    const month = String(previous.getUTCMonth() + 1).padStart(2, '0');
    const day = String(previous.getUTCDate()).padStart(2, '0');
    return `${previous.getUTCFullYear()}-${month}-${day}`;
}

function formatCetTime(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
        timeZone: CET_ZONE,
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
    });
}

const HOUR_MS = 60 * 60 * 1000;

function cetWall(ms) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: CET_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date(ms));
    const pick = (type) => Number(parts.find((part) => part.type === type).value);
    return {
        year: pick('year'),
        month: pick('month'),
        day: pick('day'),
        hour: pick('hour') % 24,
        minute: pick('minute'),
        second: pick('second'),
    };
}

function cetHourMarks(minT, maxT) {
    const marks = [];
    let cursor = minT;
    // ponytail: listings longer than 4 days still get every hour; the chart scrolls.
    while (cursor <= maxT && marks.length < 96) {
        const wall = cetWall(cursor);
        if (wall.minute === 0 && wall.second === 0) {
            marks.push(cursor);
            cursor += HOUR_MS;
            continue;
        }
        const into = wall.minute * 60000 + wall.second * 1000;
        const next = cursor + (HOUR_MS - into);
        if (next > maxT) break;
        const landed = cetWall(next);
        cursor = landed.minute === 0 && landed.second === 0 ? next : next + 60000;
    }
    return marks;
}

function cetHourLabel(ms) {
    return new Date(ms).toLocaleTimeString('en-GB', {
        timeZone: CET_ZONE,
        hour: 'numeric',
        hourCycle: 'h12',
    });
}

function cetDayLabel(ms) {
    return new Date(ms).toLocaleDateString('en-GB', {
        timeZone: CET_ZONE,
        day: 'numeric',
        month: 'short',
    });
}

function cetHourKey(ms) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: CET_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date(ms));
    const pick = (type) => parts.find((part) => part.type === type).value;
    const hour = String(Number(pick('hour')) % 24).padStart(2, '0');
    return `${pick('year')}-${pick('month')}-${pick('day')}T${hour}`;
}

function daySheet(rows) {
    const total = sumNum(rows, 'total');
    const found = sumNum(rows, 'foundOwn') + sumNum(rows, 'foundOtherBay');
    const first = earliest(rows, 'firstScanAt');
    const last = latest(rows, 'lastScanAt');
    const data = rows.map((row) => ({
        Operator: operatorName(row) || '—',
        Type: operatorType(row) || '—',
        'Agency / Email': operatorContact(row) || '—',
        Scans: row.total || 0,
        'Found own': row.foundOwn || 0,
        'Other bay': row.foundOtherBay || 0,
        'Not found': row.notFound || 0,
        'Not sent to Bay': row.notSentToBay || 0,
        'Success %': row.successRate || 0,
        Consistency: row.consistency == null ? '' : row.consistency,
        'First scan (CET)': formatCetTime(row.firstScanAt),
        'Last scan (CET)': formatCetTime(row.lastScanAt),
        Duration: formatScanDuration(row.firstScanAt, row.lastScanAt),
    }));
    data.push({
        Operator: 'Total',
        Type: '',
        'Agency / Email': '',
        Scans: total,
        'Found own': sumNum(rows, 'foundOwn'),
        'Other bay': sumNum(rows, 'foundOtherBay'),
        'Not found': sumNum(rows, 'notFound'),
        'Not sent to Bay': sumNum(rows, 'notSentToBay'),
        'Success %': total ? Number(((found / total) * 100).toFixed(1)) : 0,
        Consistency: '',
        'First scan (CET)': formatCetTime(first),
        'Last scan (CET)': formatCetTime(last),
        Duration: formatDurationMs(totalDurationMs(rows)),
    });
    return data;
}

function DayPanel() {
    const [date, setDate] = useState(cetShiftDate);
    const [sorter, setSorter] = useState({});
    const { data, isFetching } = useMinibatchDay(date);
    const payload = data?.data || {};
    const rows = payload.data || [];
    const range = payload.range;

    return (
        <>
            <div className={styles.toolbar}>
                <DatePicker
                    allowClear={false}
                    value={dayjs(date)}
                    onChange={(value) => {
                        if (value) setDate(value.format('YYYY-MM-DD'));
                    }}
                />
                <Typography.Text type="secondary">
                    {range
                        ? `${formatCetTime(range.start)} – ${formatCetTime(range.end)} Europe/Berlin`
                        : '6:30 AM to next 6:30 AM, Europe/Berlin'}
                </Typography.Text>
                <span style={{ marginLeft: 'auto' }}>
                    <ExportExcel
                        buttonSize="middle"
                        excelData={daySheet(sortedRows(rows, sorter))}
                        fileName={`minibatch-day-${date}`}
                    />
                </span>
            </div>
            <Table
                className={styles.table}
                loading={isFetching}
                pagination={false}
                sticky
                size="small"
                rowKey={(row) => row.operator?.userId || row.operator?.email || row.operator?.name}
                dataSource={rows}
                onChange={(_, __, nextSorter) => setSorter(nextSorter)}
                scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
                locale={{ emptyText: 'No scans in this window' }}
                expandable={{
                    expandedRowRender: (row) => (
                        <BayHistogram
                            histogram={row.histogram}
                            kinds={SCAN_BUCKETS}
                            timeZone={CET_ZONE}
                            zoneLabel="CET"
                            emptyText="No scans in this window"
                        />
                    ),
                    rowExpandable: (row) => (row.histogram || []).length > 0,
                }}
                summary={() => {
                    const total = sumNum(rows, 'total');
                    const found = sumNum(rows, 'foundOwn') + sumNum(rows, 'foundOtherBay');
                    const rate = total ? Number(((found / total) * 100).toFixed(1)) : 0;
                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{ fontWeight: 600 }}>
                                <Table.Summary.Cell index={0} />
                                <Table.Summary.Cell index={1}>Total</Table.Summary.Cell>
                                <Table.Summary.Cell index={2} />
                                <Table.Summary.Cell index={3} />
                                <Table.Summary.Cell index={4} align="right">{sumNum(rows, 'total')}</Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align="right">{sumNum(rows, 'foundOwn')}</Table.Summary.Cell>
                                <Table.Summary.Cell index={6} align="right">{sumNum(rows, 'foundOtherBay')}</Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align="right">{sumNum(rows, 'notFound')}</Table.Summary.Cell>
                                <Table.Summary.Cell index={8} align="right">{sumNum(rows, 'notSentToBay')}</Table.Summary.Cell>
                                <Table.Summary.Cell index={9} align="right">{rate}</Table.Summary.Cell>
                                <Table.Summary.Cell index={10} />
                                <Table.Summary.Cell index={11}>{formatCetTime(earliest(rows, 'firstScanAt'))}</Table.Summary.Cell>
                                <Table.Summary.Cell index={12}>{formatCetTime(latest(rows, 'lastScanAt'))}</Table.Summary.Cell>
                                <Table.Summary.Cell index={13} align="right">{formatDurationMs(totalDurationMs(rows))}</Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
                columns={[
                    {
                        title: 'Operator',
                        sorter: textSort(operatorName),
                        render: (_, row) => operatorName(row) || '—',
                    },
                    {
                        title: 'Type',
                        width: 110,
                        sorter: textSort(operatorType),
                        render: (_, row) => {
                            const type = operatorType(row);
                            if (!type) return '—';
                            return <Tag style={{ margin: 0 }}>{type}</Tag>;
                        },
                    },
                    {
                        title: 'Agency / Email',
                        sorter: textSort(operatorContact),
                        render: (_, row) => operatorContact(row) || '—',
                    },
                    { title: 'Scans', dataIndex: 'total', align: 'right', width: 80, sorter: numSort('total') },
                    { title: 'Found own', dataIndex: 'foundOwn', align: 'right', width: 100, sorter: numSort('foundOwn') },
                    { title: 'Other bay', dataIndex: 'foundOtherBay', align: 'right', width: 100, sorter: numSort('foundOtherBay') },
                    { title: 'Not found', dataIndex: 'notFound', align: 'right', width: 100, sorter: numSort('notFound') },
                    { title: 'Not sent to Bay', dataIndex: 'notSentToBay', align: 'right', width: 130, sorter: numSort('notSentToBay') },
                    { title: 'Success %', dataIndex: 'successRate', align: 'right', width: 100, sorter: numSort('successRate') },
                    {
                        title: 'Consistency',
                        dataIndex: 'consistency',
                        width: 110,
                        align: 'center',
                        sorter: numSort('consistency'),
                        render: (score) => <ConsistencyDial score={score} />,
                    },
                    {
                        title: 'First scan',
                        dataIndex: 'firstScanAt',
                        width: 160,
                        sorter: dateSort('firstScanAt'),
                        render: formatCetTime,
                    },
                    {
                        title: 'Last scan',
                        dataIndex: 'lastScanAt',
                        width: 160,
                        sorter: dateSort('lastScanAt'),
                        render: formatCetTime,
                    },
                    {
                        title: 'Duration',
                        align: 'right',
                        width: 100,
                        sorter: (a, b) => durationMs(a) - durationMs(b),
                        render: (_, row) => formatScanDuration(row.firstScanAt, row.lastScanAt),
                    },
                ]}
            />
        </>
    );
}

function listingChartWidth(points, scanHours = []) {
    const times = points.map((point) => new Date(point.ts).getTime());
    const scanTimes = scanHours.map((bucket) => new Date(bucket.ts).getTime()).filter((t) => !Number.isNaN(t));
    const rangeMin = Math.min(times[0], ...(scanTimes.length ? scanTimes : [times[0]]));
    const rangeMax = Math.max(times[times.length - 1], ...(scanTimes.length ? scanTimes : [times[times.length - 1]]));
    const hourMarks = cetHourMarks(rangeMin - HOUR_MS, rangeMax).filter((t) => t <= rangeMax && t + HOUR_MS > rangeMin);
    return Math.max(720, 48 + 40 + Math.max(hourMarks.length, 1) * 56);
}

function listingChartHeight(chartWidth) {
    const displayed = Math.min(chartWidth, window.innerWidth - 64);
    const room = Math.max(320, window.innerHeight - 280);
    return Math.round(chartWidth * room / Math.max(displayed, 1));
}

function ListingSuccessChart({ points, scanHours = [], fileName = 'listing-success' }) {
    const [hover, setHover] = useState(null);
    const shotRef = useRef(null);
    const times = points.map((point) => new Date(point.ts).getTime());
    const scanTimes = scanHours.map((bucket) => new Date(bucket.ts).getTime()).filter((t) => !Number.isNaN(t));
    const rangeMin = Math.min(times[0], ...(scanTimes.length ? scanTimes : [times[0]]));
    const rangeMax = Math.max(times[times.length - 1], ...(scanTimes.length ? scanTimes : [times[times.length - 1]]));
    const hourMarks = cetHourMarks(rangeMin - HOUR_MS, rangeMax).filter((t) => t <= rangeMax && t + HOUR_MS > rangeMin);
    const minT = hourMarks.length ? Math.min(rangeMin, hourMarks[0]) : rangeMin;
    const maxT = rangeMax;
    const span = Math.max(1, maxT - minT);
    const axisMarks = hourMarks.length ? hourMarks : [minT, maxT].filter((t, index, all) => all.indexOf(t) === index);
    const scansByHour = new Map();
    const peopleByHour = new Map();
    scanHours.forEach((bucket) => {
        const t = new Date(bucket.ts).getTime();
        if (Number.isNaN(t)) return;
        const key = cetHourKey(t);
        scansByHour.set(key, bucket.count || 0);
        peopleByHour.set(key, bucket.people || 0);
    });
    const width = listingChartWidth(points, scanHours);
    const downloadShot = async () => {
        const root = shotRef.current;
        if (!root) return;
        setHover(null);
        await new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        try {
            const dataUrl = await toPng(root, { cacheBust: true, pixelRatio: 2, backgroundColor: '#ffffff' });
            const link = document.createElement('a');
            link.download = `${fileName}-success.png`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            message.error('Could not export screenshot');
        }
    };
    const height = listingChartHeight(width);
    const pad = { left: 48, right: 40, top: 22, bottom: 44 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const xOf = (t) => pad.left + ((t - minT) / span) * innerW;
    const yOf = (rate) => pad.top + (1 - Math.max(0, Math.min(100, rate)) / 100) * innerH;
    const path = points
        .map((point, index) => `${index ? 'L' : 'M'}${xOf(times[index]).toFixed(1)},${yOf(point.successRate).toFixed(1)}`)
        .join(' ');
    const yTicks = [0, 25, 50, 75, 100];
    const rateAt = (t) => {
        let rate = 0;
        for (let index = 0; index < times.length; index += 1) {
            if (times[index] > t) break;
            rate = points[index].successRate;
        }
        return rate;
    };
    let previousDay = '';
    const xTicks = axisMarks.map((t) => {
        const day = cetDayLabel(t);
        const showDay = day !== previousDay;
        previousDay = day;
        const rate = rateAt(t);
        const scans = hourMarks.length
            ? scansByHour.get(cetHourKey(t)) || 0
            : scanHours.reduce((sum, bucket) => sum + (bucket.count || 0), 0);
        const people = hourMarks.length
            ? peopleByHour.get(cetHourKey(t)) || 0
            : scanHours.reduce((max, bucket) => Math.max(max, bucket.people || 0), 0);
        return {
            t,
            showDay,
            day,
            label: hourMarks.length ? cetHourLabel(t) : formatCetTime(t),
            rate,
            rateLabel: `${Number.isInteger(rate) ? rate : rate}%`,
            scans,
            people,
        };
    });
    const maxScans = Math.max(0, ...xTicks.map((tick) => tick.scans));
    const scanScale = Math.max(1, maxScans);
    const yOfScans = (count) => pad.top + innerH - (count / scanScale) * innerH;
    const barWidth = Math.min(22, Math.max(6, (innerW / Math.max(axisMarks.length, 1)) * 0.45));
    const scanTicks = maxScans <= 1 ? [0, maxScans] : [0, Math.round(maxScans / 2), maxScans];
    const maxPeople = Math.max(0, ...xTicks.map((tick) => tick.people));
    const peopleScale = Math.max(1, maxPeople);
    const yOfPeople = (count) => pad.top + innerH - (count / peopleScale) * innerH;
    const peoplePath = xTicks
        .map((tick, index) => `${index ? 'L' : 'M'}${xOf(tick.t).toFixed(1)},${yOfPeople(tick.people).toFixed(1)}`)
        .join(' ');
    const active = hover == null ? null : xTicks[hover];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Button size="small" icon={<CameraOutlined />} onClick={downloadShot}>
                    Download screenshot
                </Button>
            </div>
            <div ref={shotRef}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 12, color: '#667085' }}>
                    <span><span style={{ display: 'inline-block', width: 12, height: 3, marginRight: 6, background: '#2e90fa', verticalAlign: 'middle' }} />Success %</span>
                    <span><span style={{ display: 'inline-block', width: 12, height: 8, marginRight: 6, background: '#84caff', verticalAlign: 'middle' }} />Scans per hour</span>
                    <span><span style={{ display: 'inline-block', width: 12, height: 3, marginRight: 6, background: '#12b76a', verticalAlign: 'middle' }} />People scanning</span>
                </div>
                <div>
                    <svg
                        viewBox={`0 0 ${width} ${height}`}
                        width="100%"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                        role="img"
                        aria-label="Listing success and scans over time"
                        onMouseLeave={() => setHover(null)}
                        onMouseMove={(event) => {
                            const rect = event.currentTarget.getBoundingClientRect();
                            const x = ((event.clientX - rect.left) / rect.width) * width;
                            let best = 0;
                            let bestDist = Infinity;
                            axisMarks.forEach((t, index) => {
                                const dist = Math.abs(xOf(t) - x);
                                if (dist < bestDist) {
                                    bestDist = dist;
                                    best = index;
                                }
                            });
                            setHover(best);
                        }}
                    >
                        {yTicks.map((tick) => (
                            <g key={tick}>
                                <line x1={pad.left} x2={width - pad.right} y1={yOf(tick)} y2={yOf(tick)} stroke="#f0f0f0" />
                                <text x={pad.left - 8} y={yOf(tick)} textAnchor="end" dominantBaseline="central" fontSize="11" fill="#8c8c8c">
                                    {tick}
                                </text>
                            </g>
                        ))}
                        {maxScans > 0 ? scanTicks.map((tick) => (
                            <text key={`scans-${tick}`} x={width - pad.right + 8} y={yOfScans(tick)} textAnchor="start" dominantBaseline="central" fontSize="11" fill="#84caff">
                                {tick}
                            </text>
                        )) : null}
                        {xTicks.map((tick) => (
                            <line
                                key={`grid-${tick.t}`}
                                x1={xOf(tick.t)}
                                x2={xOf(tick.t)}
                                y1={pad.top}
                                y2={pad.top + innerH}
                                stroke="#f0f0f0"
                            />
                        ))}
                        {xTicks.map((tick) => {
                            if (!tick.scans) return null;
                            const barH = (tick.scans / scanScale) * innerH;
                            return (
                                <rect
                                    key={`bar-${tick.t}`}
                                    x={xOf(tick.t) - barWidth / 2}
                                    y={pad.top + innerH - barH}
                                    width={barWidth}
                                    height={barH}
                                    fill="#84caff"
                                />
                            );
                        })}
                        <path d={path} fill="none" stroke="#2e90fa" strokeWidth="2" />
                        {maxPeople > 0 ? <path d={peoplePath} fill="none" stroke="#12b76a" strokeWidth="2" /> : null}
                        {points.length === 1 ? (
                            <circle cx={xOf(times[0])} cy={yOf(points[0].successRate)} r="4" fill="#2e90fa" />
                        ) : null}
                        {xTicks.map((tick) => {
                            const x = xOf(tick.t);
                            const y = yOf(tick.rate);
                            const labelY = y < pad.top + 16 ? y + 14 : y - 8;
                            const countY = yOfScans(tick.scans) - 4;
                            const showCount = tick.scans > 0 && Math.abs(countY - labelY) >= 12;
                            return (
                                <g key={tick.t}>
                                    <circle cx={x} cy={y} r="3" fill="#2e90fa" />
                                    {showCount ? (
                                        <text x={x} y={countY} textAnchor="middle" fontSize="10" fill="#175cd3">
                                            {tick.scans}
                                        </text>
                                    ) : null}
                                    <text x={x} y={labelY} textAnchor="middle" fontSize="10" fill="#344054">
                                        {tick.rateLabel}
                                    </text>
                                    {tick.people > 0 ? (
                                        <text x={x + 8} y={yOfPeople(tick.people)} textAnchor="start" dominantBaseline="central" fontSize="10" fill="#027a48">
                                            {tick.people}
                                        </text>
                                    ) : null}
                                    {tick.showDay ? (
                                        <text x={x} y={height - 22} textAnchor="middle" fontSize="10" fill="#98a2b3">
                                            {tick.day}
                                        </text>
                                    ) : null}
                                    <text x={x} y={height - 8} textAnchor="middle" fontSize="11" fill="#667085">
                                        {tick.label}
                                    </text>
                                </g>
                            );
                        })}
                        {active ? (
                            <g>
                                <line
                                    x1={xOf(active.t)}
                                    x2={xOf(active.t)}
                                    y1={pad.top}
                                    y2={pad.top + innerH}
                                    stroke="#98a2b3"
                                    strokeDasharray="3 3"
                                />
                                <circle cx={xOf(active.t)} cy={yOf(active.rate)} r="4" fill="#2e90fa" />
                            </g>
                        ) : null}
                    </svg>
                </div>
                <Typography.Text type="secondary" style={{ display: 'block' }}>
                    {active
                        ? `${active.showDay ? `${active.day} ` : ''}${active.label} CET · ${active.rateLabel} · ${active.scans} scans · ${active.people} people`
                        : 'Success is cumulative unique SKUs found. Bars are scans per Europe/Berlin hour. The green line is distinct people scanning in that hour.'}
                </Typography.Text>
            </div>
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div className={styles.metric}>
            <div className={styles.metricLabel}>{label}</div>
            <div className={styles.metricValue}>{value ?? '—'}</div>
        </div>
    );
}

export default function MinibatchPerformance() {
    const [section, setSection] = useState('listings');
    const [sessionPage, setSessionPage] = useState(1);
    const [sessionSearch, setSessionSearch] = useState('');
    const [selectedSession, setSelectedSession] = useState(null);
    const [view, setView] = useState('operators');
    const [operatorSorter, setOperatorSorter] = useState({});
    const [guidelineSorter, setGuidelineSorter] = useState({});
    const [successOpen, setSuccessOpen] = useState(false);
    const { data: sessionList, isFetching: sessionsLoading } = useGetMergedGuidelinesForShoot(sessionPage, 10, sessionSearch);
    const { data, isFetching } = useMinibatchReport(
        { inventorySessionId: selectedSession?.sessionId },
        { enabled: !!selectedSession?.sessionId }
    );
    const payload = data?.data || {};

    const listingSummary = payload.listingSummary;

    if (!selectedSession) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div>
                        <Typography.Title level={4} className={styles.title}>
                            Minibatching performance
                        </Typography.Title>
                        <Typography.Text type="secondary" className={styles.meta}>
                            {section === 'day'
                                ? 'A day is 6:30 AM Europe/Berlin until 6:30 AM the next day.'
                                : 'Open a listing to see operators and guidelines.'}
                        </Typography.Text>
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Radio.Group value={section} onChange={(event) => setSection(event.target.value)}>
                            <Radio.Button value="listings">Listings</Radio.Button>
                            <Radio.Button value="day">By day</Radio.Button>
                        </Radio.Group>
                        {section === 'listings' ? (
                            <Input.Search
                                allowClear
                                placeholder="Search listing name"
                                style={{ width: 280 }}
                                onSearch={(value) => {
                                    setSessionSearch(value);
                                    setSessionPage(1);
                                }}
                            />
                        ) : null}
                    </div>
                </div>
                {section === 'day' ? <DayPanel /> : null}
                {section === 'listings' ? <Table
                    className={styles.listings}
                    loading={sessionsLoading}
                    size="middle"
                    rowKey={(row) => row.sessionId || row._id}
                    dataSource={sessionList?.data || []}
                    pagination={{
                        current: sessionPage,
                        pageSize: 10,
                        total: sessionList?.totalCount || 0,
                        onChange: setSessionPage,
                        showSizeChanger: false,
                    }}
                    onRow={(row) => ({
                        onClick: () => {
                            setView('operators');
                            setSelectedSession(row);
                        },
                        style: { cursor: 'pointer' },
                    })}
                    columns={[
                        { title: 'Listing', dataIndex: 'name' },
                        { title: 'Team lead', dataIndex: 'teamLead', width: 160, render: (value) => value || '—' },
                        {
                            title: 'Session ID',
                            dataIndex: 'sessionId',
                            width: 140,
                            render: (value) => <Typography.Text type="secondary">{value}</Typography.Text>,
                        },
                        {
                            title: 'Guidelines',
                            align: 'right',
                            width: 110,
                            render: (_, row) => (row.mergeIds || []).length,
                        },
                        { title: 'Created by', width: 140, render: (_, row) => row.createdBy?.name || '—' },
                        {
                            title: 'Created at',
                            dataIndex: 'createdAt',
                            width: 190,
                            render: formatScanTime,
                        },
                    ]}
                /> : null}
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <Button
                        type="link"
                        style={{ padding: 0, height: 'auto' }}
                        onClick={() => {
                            setSelectedSession(null);
                            setView('operators');
                            setSuccessOpen(false);
                        }}
                    >
                        Back to listings
                    </Button>
                    <Typography.Title level={4} className={styles.title}>
                        {selectedSession.name || selectedSession.sessionId}
                    </Typography.Title>
                    <Typography.Text type="secondary" className={styles.meta}>
                        {[selectedSession.teamLead, selectedSession.sessionId, `Times in ${localTimeZone}`]
                            .filter(Boolean)
                            .join(' · ')}
                    </Typography.Text>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button onClick={() => setSuccessOpen(true)} disabled={!listingSummary}>
                        Success over time
                    </Button>
                    <ExportExcel
                        buttonSize="middle"
                        excelData={
                            view === 'operators'
                                ? operatorSheet(sortedRows(payload.data || [], operatorSorter))
                                : guidelineSheet(sortedRows(payload.byGuideline || [], guidelineSorter))
                        }
                        fileName={`${fileSlug(selectedSession.name || selectedSession.sessionId)}-${view}`}
                    />
                </div>
            </div>
            <Modal
                title={`Listing success · ${selectedSession.name || selectedSession.sessionId}`}
                open={successOpen}
                footer={null}
                width={Math.min(
                    ((payload.listingTimeline?.points || []).length
                        ? listingChartWidth(payload.listingTimeline.points, payload.listingTimeline.scanHours || [])
                        : 720) + 48,
                    window.innerWidth - 16
                )}
                style={{ top: 24 }}
                bodyStyle={{ overflow: 'visible' }}
                destroyOnClose
                onCancel={() => setSuccessOpen(false)}
            >
                {(payload.listingTimeline?.points || []).length ? (
                    <ListingSuccessChart
                        points={payload.listingTimeline.points}
                        scanHours={payload.listingTimeline.scanHours || []}
                        fileName={fileSlug(selectedSession.name || selectedSession.sessionId)}
                    />
                ) : (
                    <Typography.Text type="secondary">
                        {isFetching ? 'Loading scans…' : 'No SKUs found on this listing yet.'}
                    </Typography.Text>
                )}
            </Modal>
            {listingSummary ? (
                <div className={styles.metrics}>
                    <Metric
                        label="Listing success"
                        value={listingSummary.successRate == null ? '—' : `${listingSummary.successRate}%`}
                    />
                    <Metric label="Combined found" value={listingSummary.combinedFound} />
                    <Metric label="Expected SKUs" value={listingSummary.expectedSkus} />
                    <Metric label="Remaining" value={listingSummary.remaining} />
                    <Metric label="Guidelines" value={listingSummary.guidelineCount} />
                </div>
            ) : null}
            <div className={styles.toolbar}>
                <Radio.Group value={view} onChange={(e) => setView(e.target.value)}>
                    <Radio.Button value="operators">Operators</Radio.Button>
                    <Radio.Button value="guidelines">Guidelines</Radio.Button>
                </Radio.Group>
            </div>
            {view === 'operators' ? (
                <Table
                    className={styles.table}
                    loading={isFetching}
                    pagination={false}
                    sticky
                    size="small"
                    rowKey={(r) => r.operator?.userId || r.operator?.email || r.operator?.name}
                    dataSource={payload.data || []}
                    onChange={(_, __, sorter) => setOperatorSorter(sorter)}
                    scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
                    summary={() => {
                        const rows = payload.data || [];
                        const total = sumNum(rows, 'total');
                        const found = sumNum(rows, 'foundOwn') + sumNum(rows, 'foundOtherBay');
                        const first = earliest(rows, 'firstScanAt');
                        const last = latest(rows, 'lastScanAt');
                        const rate = total ? Number(((found / total) * 100).toFixed(1)) : 0;
                        return (
                            <Table.Summary fixed>
                                <Table.Summary.Row style={{ fontWeight: 600 }}>
                                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} />
                                    <Table.Summary.Cell index={2} />
                                    <Table.Summary.Cell index={3} align="right">{sumNum(rows, 'total')}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">{sumNum(rows, 'foundOwn')}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">{sumNum(rows, 'foundOtherBay')}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="right">{sumNum(rows, 'notFound')}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={7} align="right">{sumNum(rows, 'notSentToBay')}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={8} align="right">{rate}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={9}>{formatScanTime(first)}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={10}>{formatScanTime(last)}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={11} align="right">{formatDurationMs(totalDurationMs(rows))}</Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                    columns={[
                        {
                            title: 'Operator',
                            sorter: textSort(operatorName),
                            render: (_, r) => operatorName(r) || '—',
                        },
                        {
                            title: 'Type',
                            width: 110,
                            sorter: textSort(operatorType),
                            render: (_, r) => {
                                const type = operatorType(r);
                                if (!type) return '—';
                                return <Tag style={{ margin: 0 }}>{type}</Tag>;
                            },
                        },
                        {
                            title: 'Agency / Email',
                            sorter: textSort(operatorContact),
                            render: (_, r) => operatorContact(r) || '—',
                        },
                        { title: 'Scans', dataIndex: 'total', align: 'right', width: 80, sorter: numSort('total') },
                        { title: 'Found own', dataIndex: 'foundOwn', align: 'right', width: 100, sorter: numSort('foundOwn') },
                        { title: 'Other bay', dataIndex: 'foundOtherBay', align: 'right', width: 100, sorter: numSort('foundOtherBay') },
                        { title: 'Not found', dataIndex: 'notFound', align: 'right', width: 100, sorter: numSort('notFound') },
                        { title: 'Not sent to Bay', dataIndex: 'notSentToBay', align: 'right', width: 130, sorter: numSort('notSentToBay') },
                        { title: 'Success %', dataIndex: 'successRate', align: 'right', width: 100, sorter: numSort('successRate') },
                        {
                            title: 'First scan',
                            dataIndex: 'firstScanAt',
                            width: 170,
                            sorter: dateSort('firstScanAt'),
                            render: formatScanTime,
                        },
                        {
                            title: 'Last scan',
                            dataIndex: 'lastScanAt',
                            width: 170,
                            sorter: dateSort('lastScanAt'),
                            render: formatScanTime,
                        },
                        {
                            title: 'Duration',
                            align: 'right',
                            width: 100,
                            sorter: (a, b) => durationMs(a) - durationMs(b),
                            render: (_, row) => formatScanDuration(row.firstScanAt, row.lastScanAt),
                        },
                    ]}
                />
            ) : (
                <Table
                    className={styles.table}
                    loading={isFetching}
                    pagination={false}
                    sticky
                    size="small"
                    rowKey="mergeId"
                    dataSource={payload.byGuideline || []}
                    onChange={(_, __, sorter) => setGuidelineSorter(sorter)}
                    scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
                    locale={{ emptyText: 'No guidelines in this listing' }}
                    summary={() => {
                        const rows = payload.byGuideline || [];
                        const expected = sumNum(rows, 'expectedSkus');
                        const combined = sumNum(rows, 'combinedFound');
                        const pct = expected ? Number(((combined / expected) * 100).toFixed(1)) : null;
                        return (
                            <Table.Summary fixed>
                                <Table.Summary.Row style={{ fontWeight: 600 }}>
                                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">{expected}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} align="right">{sumNum(rows, 'foundOwn')}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="right">{sumNum(rows, 'foundFromOther')}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">{combined}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">{sumNum(rows, 'remaining')}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="right">{pct == null ? '—' : `${pct}%`}</Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                    columns={[
                        { title: 'Guideline', dataIndex: 'guidelineName', sorter: textSort((row) => row.guidelineName) },
                        { title: 'SKUs on guideline', dataIndex: 'expectedSkus', align: 'right', width: 150, sorter: numSort('expectedSkus') },
                        { title: 'Found here', dataIndex: 'foundOwn', align: 'right', width: 110, sorter: numSort('foundOwn') },
                        { title: 'Found from other scan', dataIndex: 'foundFromOther', align: 'right', width: 180, sorter: numSort('foundFromOther') },
                        { title: 'Combined found', dataIndex: 'combinedFound', align: 'right', width: 140, sorter: numSort('combinedFound') },
                        { title: 'Remaining', dataIndex: 'remaining', align: 'right', width: 110, sorter: numSort('remaining') },
                        {
                            title: 'Combined %',
                            dataIndex: 'combinedPct',
                            align: 'right',
                            width: 120,
                            sorter: numSort('combinedPct'),
                            render: (v) => (v == null ? '—' : `${v}%`),
                        },
                    ]}
                />
            )}
        </div>
    );
}
